import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import type {
	IBundleShoppingProduct,
	IDexfSettings,
	IKompisTranslations,
	ILocalizedInformation,
	IProduct,
	IShoppingProduct,
	IStore,
	IStoreAvailability,
	ITheme
} from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type { IMediaItem } from '@inter-ikea-kompis/component-media-carousel';
import type { IProductListSection } from '@inter-ikea-kompis/component-product-list';
import {
	ProductRowLayoutEnum,
	ProductRowProductLinkEnum,
	ProductRowProductMeasureEnum
} from '@inter-ikea-kompis/component-product-row';
import { SortListOptionsEnum } from '@inter-ikea-kompis/component-sort-list';

import { Text } from '@ingka/common-styles-webc';
import { informationCircle, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { Tabs as SkapaTabs } from '@ingka/tabs-webc/passive.js';

import PrintableContentEnum from '../../enums/PrintableContentEnum.js';
import SummaryPageTabEnum from '../../enums/SummaryPageTabEnum.js';
import type { ISummaryPageCustomTabButton } from '../../types/ISummaryPageCustomTabButton.js';
import type { ISummaryPageHomeDeliveryProps } from '../../types/ISummaryPageHomeDeliveryProps.js';
import type { ISummaryPageSecurityInformation } from '../../types/ISummaryPageSecurityInformation.js';
import type { ISummaryPageVisibleModal } from '../../types/ISummaryPageVisibleModal.js';
import SummaryPageCustomTabsUtility from '../../utilities/SummaryPageCustomTabsUtility.js';
import SummaryPageImages from '../summary-page-images/SummaryPageImages.js';
import SummaryPageProductList from '../summary-page-product-list/SummaryPageProductList.js';
import SummaryPageSecurity from '../summary-page-security/SummaryPageSecurity.js';
import SummaryPageTabsStyle from './SummaryPageTabsStyle.js';

/**
 * @event storeSelectorChange Triggered when the selected store changes.
 * @event sortListChange Triggered when the selected way of sorting changes.
 * @event modalOpen Triggered when a sheet is opened.
 * @event modalClose Triggered when the user closes a sheet.
 * @event tabChange Triggered when the selected tab changes.
 * @event selectedImageIndexChange Triggered when the selected image changes in the "Images" tab.
 * @event collectProduct Triggered when a product is collected.
 * @event homeDeliveryClick User clicks on the home delivery link to select zip code.
 * @event productListAdd Triggered when the add button is clicked on a product in product list.
 * @event productListRemove Triggered when the remove button is clicked on a product in product list.
 * @event productListExpand Triggered when included shopping products in product list gets expanded.
 * @event productListNameClick Triggered when the name of a product is clicked.
 *
 * @slot custom-print-content - Slot for custom content to be included in the print layout.
 * @slot custom-tab-1 - Slot for custom tab content.
 * @slot custom-tab-2 - Slot for custom tab content.
 * @slot custom-tab-3 - Slot for custom tab content.
 * @slot custom-tab-4 - Slot for custom tab content.
 * @slot custom-tab-5 - Slot for custom tab content.
 */
export default class SummaryPageTabs extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IKompisTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// A list of shopping products or bundle shopping products.
	@property({ type: Array, required: true })
	public shoppingProducts!: (IShoppingProduct | IBundleShoppingProduct)[];

	// A list of store availability objects.
	@property({ type: Array })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// A list of stores, when left empty the store selector will be hidden
	@property({ type: Array })
	public stores: IStore[] | null = null;

	// The storeId of the selected store
	@property({ type: String })
	public storeId: string | null = null;

	// Controls how measurements should be shown in the Product list tab
	@property({ type: String })
	public productMeasures: ProductRowProductMeasureEnum = ProductRowProductMeasureEnum.none;

	// Disables outgoing links. Should be true for kiosks.
	@property({ type: Boolean })
	public disableLinks = false;

	// The currently visible sheet.
	@property({ type: Object })
	public visibleModal: ISummaryPageVisibleModal | null = null;

	// A list of images used in the "Images" tab
	@property({ type: Array })
	public images: IMediaItem[] | null = null;

	// An object with information for the security tab
	@property({ type: Object })
	public securityInformation: ISummaryPageSecurityInformation | null = null;

	// A collection of props used for the home delivery availability in product list. For more info about the props check the HomeDelivery component.
	@property({ type: Object, synchronous: true })
	public homeDelivery: ISummaryPageHomeDeliveryProps | null = null;

	// Zip code will be passed down to home delivery.
	@property({ type: String })
	public zipCode: string | null = null;

	// Whether to show the collect checkbox in the product list or not.
	@property({ type: Boolean })
	public collectableProducts = false;

	// A list of product ids that are collected.
	@property({ type: Array })
	public collectedProducts: string[] | null = null;

	// Show text describing the color and/or material of the product.
	@property({ type: Boolean })
	public showDesignText = false;

	// A list of sections used in the product list.
	@property({ type: Array })
	public productListSections: IProductListSection[] | null = null;

	// Defines optional custom tab buttons. Up to five tabs are supported.
	@property({ type: Object })
	public customTabButtons: ISummaryPageCustomTabButton[] | null = null;

	// Highest heading in the summary page tabs.
	@property({ type: Number })
	public startHeadingsAtLevel: 3 | 4 = 3;

	// A list of included products within SPR products. Must be set (if applicable) for total package weight.
	@property({ type: Array })
	public includedProducts: IProduct[] | null = null;

	// A list of product item numbers that can be added in the product list.
	@property({ type: Array })
	public productListAddable: string[] | null = null;

	// A list of product item numbers that can be removed in the product list.
	@property({ type: Array })
	public productListRemovable: string[] | null = null;

	// Sets the default sorting of the product list.
	@property({ type: String })
	public defaultSortBy: SortListOptionsEnum | null = null;

	// A sorted list of content to include in the print layout. When not set, the currently shown tab is printed. To include custom content in the print, the content must also be included as the slot "custom-print-content". E.g. ['productListTab', 'imagesTab', 'securityTab', 'customPrintContent'].
	@property({ type: Object })
	public contentToPrint: PrintableContentEnum[] | null = null;

	// Layout for summary page. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print. Make sure SPR's has their child articles in includedProducts, since SPR's are not visible in compactPrint.
	@property({ type: String })
	public pickingListPrintLayout: ProductRowLayoutEnum | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	// Recommended pattern for normal and kiosk mode, is eventOnly to open a ProductInformationCard in a Skapa sheet modal. If using productInformationPage, disable on kiosk.
	@property({ type: String })
	public productListLink: ProductRowProductLinkEnum =
		ProductRowProductLinkEnum.productInformationPage;

	private numberOfCustomTabs = 0;

	public override render(): RenderResult | null {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html` <style>
				${SummaryPageTabsStyle.getStyle({
					theme: this.theme,
					hideSkapaTabsOnPrint: !!this.contentToPrint
				})}
			</style>
			${this.getTabs()}${this.getContentToPrint()}`;
	}
	public override async connectedCallback(): Promise<void> {
		registerIcon(warningTriangle);
		registerIcon(informationCircle);

		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-tabs', SkapaTabs);
		this.customElements.define('skapa-image', SkapaImage);
		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('kompis-summary-page-product-list', SummaryPageProductList);
		this.customElements.define('kompis-summary-page-images', SummaryPageImages);
		this.customElements.define('kompis-summary-page-security', SummaryPageSecurity);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('customTabButtons')) {
			this.numberOfCustomTabs = SummaryPageCustomTabsUtility.getNumberOfCustomTabsToShow(
				this.customTabButtons
			);
		}
		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation;
	}

	private getTranslations(): IKompisTranslations {
		return this.translations
			? this.translations
			: <IKompisTranslations>this.localizedInformation?.translations;
	}

	private showImagesTab(): boolean {
		return !!this.images?.length;
	}

	private showSecurityTab(): boolean {
		return !!this.securityInformation;
	}

	private getTabName(index: number): SummaryPageTabEnum {
		const tabsInOrder = [SummaryPageTabEnum.productList];

		if (this.showImagesTab()) {
			tabsInOrder.push(SummaryPageTabEnum.images);
		}

		if (this.showSecurityTab()) {
			tabsInOrder.push(SummaryPageTabEnum.security);
		}

		for (let n = 1; n <= this.numberOfCustomTabs; n++) {
			tabsInOrder.push(SummaryPageTabEnum[`customTab${n}`]);
		}

		return tabsInOrder[index];
	}

	private getTabHeading(tabName: string): HTMLElement {
		const tagName = `h${this.startHeadingsAtLevel}`;
		const element = document.createElement(tagName);
		element.textContent = tabName;
		element.className = 'visuallyHidden';
		return element;
	}

	private getCustomTabs(): Array<RenderResult> {
		const tabs: RenderResult = [];

		for (let n = 1; n <= this.numberOfCustomTabs; n++) {
			const label = this.customTabButtons?.[n - 1]?.label;
			tabs.push(html`
				<button>${label}</button>
				<div slot="panels">
					${label && this.getTabHeading(label)}
					<slot slot="custom-tab-${n}" name="custom-tab-${n}"></slot>
				</div>
			`);
		}

		return tabs;
	}

	private getTabs(): RenderResult {
		return html` <skapa-tabs
			auto-id="summary-page-tabs"
			@skapa-tab-select="${({ detail: targetIndex }) => {
				const tabName = this.getTabName(targetIndex);
				this.dispatchEvent(
					new CustomEvent('tabChange', {
						bubbles: true,
						detail: { selectedTab: tabName }
					})
				);
				this.requestUpdate();
			}}"
		>
			<button>${this.getTranslations().summaryPageProductListTab}</button>
			<div slot="panels">
				${this.getTabHeading(this.getTranslations().summaryPageProductListTab)}
				${this.getProductListTab({
					pickingListPrintLayout: ProductRowLayoutEnum.default,
					defaultSortBy: this.defaultSortBy,
					withModal: true
				})}
			</div>
			${this.showImagesTab()
				? html`
						<button>${this.getTranslations().summaryPageImagesTab}</button>
						<div slot="panels">
							${this.getTabHeading(this.getTranslations().summaryPageImagesTab)}
							${this.getImagesTab()}
						</div>
					`
				: ''}
			${this.showSecurityTab()
				? html`
						<button class="images">
							<skapa-icon slot="icon" icon="warning-triangle"></skapa-icon>
							${this.getTranslations().summaryPageSecurityTab}
						</button>
						<div slot="panels">
							${this.getTabHeading(this.getTranslations().summaryPageSecurityTab)}
							${this.getSecurityTab()}
						</div>
					`
				: ''}
			${this.getCustomTabs()}
		</skapa-tabs>`;
	}

	private getProductListTab(options: {
		pickingListPrintLayout: ProductRowLayoutEnum | null;
		defaultSortBy: SortListOptionsEnum | null;
		withModal?: boolean;
	}): RenderResult {
		return html` <kompis-summary-page-product-list
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.shoppingProducts="${this.shoppingProducts}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.stores="${this.stores}"
			.storeId="${this.storeId}"
			.productMeasures="${this.productMeasures}"
			.visibleModal="${options.withModal ? this.visibleModal : null}"
			.disableLinks="${this.disableLinks}"
			.homeDelivery="${this.homeDelivery}"
			.pickingListPrintLayout="${options.pickingListPrintLayout}"
			.includedProducts="${this.includedProducts}"
			.collectableProducts="${this.collectableProducts}"
			.collectedProducts="${this.collectedProducts}"
			.showDesignText="${this.showDesignText}"
			.productListSections="${this.productListSections}"
			.productListAddable="${this.productListAddable}"
			.productListRemovable="${this.productListRemovable}"
			.defaultSortBy="${options.defaultSortBy}"
			.startHeadingsAtLevel="${this.startHeadingsAtLevel + 1}"
			.zipCode="${this.zipCode}"
			.measurementSystem="${this.measurementSystem}"
			.productListLink="${this.productListLink}"
			@modalOpen="${(event: CustomEvent) => {
				this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }));
			}}"
			@modalClose="${(event: CustomEvent) =>
				this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			@storeSelectorChange="${({ detail }) => {
				this.dispatchEvent(new CustomEvent('storeSelectorChange', { bubbles: true, detail }));
			}}"
			@sortListChange="${({ detail }): boolean =>
				this.dispatchEvent(
					new CustomEvent('sortListChange', {
						bubbles: true,
						detail
					})
				)}"
			@homeDeliveryClick="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('homeDeliveryClick', { bubbles: true, detail: event.detail })
				);
			}}"
			@collectProduct="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('collectProduct', { bubbles: true, detail: event.detail })
				);
			}}"
			@productListAdd="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListAdd', { bubbles: true, detail: event.detail })
				);
			}}"
			@productListRemove="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListRemove', { bubbles: true, detail: event.detail })
				);
			}}"
			@productListExpand="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListExpand', { bubbles: true, detail: event.detail })
				);
			}}"
			@productListNameClick="${(event: CustomEvent) => {
				this.dispatchEvent(
					new CustomEvent('productListNameClick', { bubbles: true, detail: { ...event.detail } })
				);
			}}"
		>
		</kompis-summary-page-product-list>`;
	}

	private getImagesTab(): RenderResult {
		return html`<kompis-summary-page-images
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.images="${this.images}"
			@change="${({ detail }): void => {
				this.dispatchEvent(
					new CustomEvent('selectedImageIndexChange', {
						bubbles: true,
						detail: { selectedIndex: detail.selectedIndex }
					})
				);
			}}"
		></kompis-summary-page-images>`;
	}
	private getSecurityTab(): RenderResult {
		if (!this.securityInformation) {
			return null;
		}

		return html`<kompis-summary-page-security
			.theme="${this.theme}"
			.title="${this.securityInformation.title}"
			.description="${this.securityInformation.description}"
			.imageSrc="${this.securityInformation.imageSrc}"
			.imageAlt="${this.securityInformation.imageAlt}"
			.startHeadingsAtLevel="${this.startHeadingsAtLevel + 1}"
		>
		</kompis-summary-page-security>`;
	}

	private getContentToPrint(): RenderResult {
		if (!this.contentToPrint?.length) {
			return null;
		}

		const uniqueContentToPrint = this.contentToPrint.filter(
			(contentToPrint, index, array) => array.indexOf(contentToPrint) === index
		);

		return html`<div class="contentToPrint">
			${uniqueContentToPrint.map((contentToPrint) => this.getContentToPrintSection(contentToPrint))}
		</div>`;
	}

	private getContentToPrintSection(printableContent: PrintableContentEnum): RenderResult {
		const header = html`<h2 class="${Text.classnames({ heading: { m: true } })}">
			${this.getHeaderText(printableContent)}
		</h2>`;

		switch (printableContent) {
			case PrintableContentEnum.productListTab:
				return html`<section>
					${header}${this.getSubHeader()}${this.getProductListTab({
						pickingListPrintLayout: this.pickingListPrintLayout,
						defaultSortBy: SortListOptionsEnum.aisleLocation
					})}
				</section>`;
			case PrintableContentEnum.imagesTab:
				return this.showImagesTab()
					? html`<section class="avoidBreak">${header}${this.getImageGrid()}</section>`
					: null;
			case PrintableContentEnum.securityTab:
				return this.showSecurityTab()
					? html`<section>${header}${this.getSecurityTab()}</section>`
					: null;
			case PrintableContentEnum.customPrintContent:
				return html`<section><slot name="custom-print-content"></slot></section>`;
		}
	}

	private getHeaderText(printableContent: PrintableContentEnum): string | null {
		switch (printableContent) {
			case PrintableContentEnum.productListTab:
				return this.getTranslations().summaryPageProductListTab;
			case PrintableContentEnum.imagesTab:
				return this.getTranslations().summaryPageImagesTab;
			case PrintableContentEnum.securityTab:
				return this.getTranslations().summaryPageSecurityTab;
			default:
				return null;
		}
	}

	private getSubHeader(): RenderResult {
		const template: RenderResult[] = [
			html`<h4 class="subheader text--heading-s">${this.getTranslations().pickingListHeader}</h4>`
		];
		if (this.pickingListPrintLayout === ProductRowLayoutEnum.compactPrint) {
			const informativeVariant: InlineMessageVariant = 'informative';
			template.push(
				html`<skapa-inline-message subtle class="message" variant="${informativeVariant}">
					<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
					<span slot="body"
						>${this.getTranslations().summaryPagePrintStockAvailabilityDisclaimer}</span
					>
				</skapa-inline-message>`
			);
		}
		template.push();

		return template;
	}

	private getImageGrid(): RenderResult {
		if (this.images?.length === 1) {
			return html`<skapa-image ?fallback="${null}">
				<img src="${this.images[0].src}" alt="" />
			</skapa-image>`;
		}

		return html`<div class="imageGrid">
			${this.images?.map(
				({ src }) => html`
					<skapa-image ?fallback="${null}">
						<img src="${src}" alt="" />
					</skapa-image>
				`
			)}
		</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-icon': SkapaIcon;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-tabs': SkapaTabs;
		'skapa-image': SkapaImage;
		'kompis-summary-page-tabs': SummaryPageTabs;
	}
}
